/**
 * Незавершённая заявка на релиз: пользователь дошёл до экрана подтверждения,
 * баланса не хватило и он ушёл платить.
 *
 * Шаг формы живёт в URL (`/release?audioId=…&step=confirm`), поэтому в вебе,
 * где оплата открывается ОТДЕЛЬНОЙ вкладкой, возвращать никуда не нужно —
 * главная вкладка так и стоит на подтверждении. Пометка нужна для второго
 * сценария: оплата в ТОЙ ЖЕ вкладке (миниапп/фолбэк при заблокированном попапе),
 * когда провайдер возвращает на `/payment/success` — оттуда уводим обратно на
 * экран подтверждения (по образцу `pendingClipAction`).
 *
 * ВАЖНО: метка ставится ДО оплаты (в момент 402) и живёт час, а пользователь
 * мог передумать и купить совсем другое — песни или клип. Поэтому возврат
 * сверяется с ОПЛАЧЕННЫМ пакетом (`isPendingReleasePayment`): по чужой оплате
 * метка не срабатывает и резюм клипа/песни отрабатывает штатно.
 */
import { isReleasePackage } from "@/shared/api/packages";
const STORAGE_KEY = "tvoy_podarok_pending_release_order";
const MAX_AGE_MS = 60 * 60 * 1000;
export function savePendingReleaseOrder(order) {
    try {
        const payload = {
            releaseId: order.releaseId,
            audioId: order.audioId,
            savedAt: Date.now(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    }
    catch {
        /* private mode / quota */
    }
}
export function getPendingReleaseOrder() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        const s = JSON.parse(raw);
        const releaseId = Number(s.releaseId);
        const audioId = Number(s.audioId);
        if (!(Number.isFinite(releaseId) && releaseId > 0))
            return null;
        if (!(Number.isFinite(audioId) && audioId > 0))
            return null;
        const savedAt = Number(s.savedAt);
        if (Number.isFinite(savedAt) && Date.now() - savedAt > MAX_AGE_MS) {
            clearPendingReleaseOrder();
            return null;
        }
        return {
            releaseId,
            audioId,
            savedAt: Number.isFinite(savedAt) ? savedAt : Date.now(),
        };
    }
    catch {
        return null;
    }
}
export function clearPendingReleaseOrder() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    }
    catch {
        /* no-op */
    }
}
/**
 * Оплачен ли пакет РЕЛИЗОВ, т.е. относится ли платёж к отложенной заявке.
 * Пакеты релизов начисляют `releases` (см. `isReleasePackage`); любая другая
 * покупка (песни, клип) к метке отношения не имеет.
 *
 * Наружу не отдаём: единственный легальный вход — `getPendingReleaseResumePath`,
 * который проверяет ещё и наличие самой метки.
 */
function isPendingReleasePayment(payment) {
    if (!payment?.package)
        return false;
    return isReleasePackage(payment.package);
}
/**
 * Путь возврата на экран подтверждения заявки — только если оплачен пакет
 * релизов и метка на месте. Иначе null (оплата чужая, уводить нельзя).
 */
export function getPendingReleaseResumePath(payment) {
    if (!isPendingReleasePayment(payment))
        return null;
    const pending = getPendingReleaseOrder();
    if (!pending)
        return null;
    return `/release?audioId=${pending.audioId}&step=confirm`;
}
